import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Switch,
  Text,
  Textarea,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Add16Regular, Delete16Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import type { Network } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { PageAside } from '../../components/PageAside';
import { vt } from '../../theme';
import {
  addressesIn,
  blankForm,
  createBody,
  deriveId,
  formOf,
  networkError,
  patchBody,
  validateForm,
  type NetworkField,
  type NetworkForm,
} from './model';

const useStyles = makeStyles({
  head: { display: 'flex', alignItems: 'center', padding: '22px 16px 14px 24px', borderBottom: `1px solid ${vt.surface2}` },
  title: { margin: 0, flexGrow: 1, fontSize: '16px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  form: { display: 'flex', flexDirection: 'column', flexGrow: 1 },
  section: { padding: '16px 24px', display: 'flex', flexDirection: 'column', rowGap: '14px', borderBottom: `1px solid ${vt.surface2}` },
  fieldLabel: { fontSize: '11.5px', fontWeight: tokens.fontWeightSemibold, color: vt.fg2, paddingBottom: '2px' },
  mono: { fontFamily: tokens.fontFamilyMonospace },
  hint: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.green },
  hintQuiet: { fontSize: '10.5px', color: vt.fg4 },
  readonly: { display: 'grid', gridTemplateColumns: '90px minmax(0, 1fr)', rowGap: '9px', columnGap: '12px', margin: 0 },
  k: { fontSize: '11.5px', color: vt.fg3 },
  v: { margin: 0, fontSize: '11.5px', color: vt.fg1, fontFamily: tokens.fontFamilyMonospace, textAlign: 'right' },
  toggleSub: { fontSize: '10.5px', color: vt.fg4, marginTop: '-8px', paddingLeft: '44px' },
  actions: { display: 'flex', alignItems: 'center', columnGap: '8px', padding: '16px 24px' },
  spacer: { flexGrow: 1 },
  action: { fontSize: '12.5px', minWidth: 0, paddingInline: '14px' },
});

type Errors = Partial<Record<NetworkField, string>>;

export interface NetworkDrawerProps {
  /** The network to edit; null = create a new one. */
  network: Network | null;
  nextFreeCidr: string | null;
  onClose: () => void;
  /** After a create, edit or delete: the id to select next (null after a delete). */
  onDone: (selectId: string | null) => Promise<void>;
}

/**
 * The right-hand pane (mockup 04): POST /networks with the id derived from the name and the next
 * free /24 prefilled, or PATCH name / isolated / note of an existing one, and DELETE behind a
 * confirm. API errors land on the field they are about, the rest above the buttons.
 */
export function NetworkDrawer({ network, nextFreeCidr, onClose, onDone }: NetworkDrawerProps) {
  const s = useStyles();
  const { api } = useAuth();
  const creating = network === null;
  const [form, setForm] = useState<NetworkForm>(() => (network ? formOf(network) : blankForm(nextFreeCidr)));
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const set = <K extends keyof NetworkForm>(key: K, value: NetworkForm[K]) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === 'name' && !f.idEdited) next.id = deriveId(String(value));
      if (key === 'id') next.idEdited = true;
      return next;
    });
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const fail = (e: unknown) => {
    const placed = networkError(e);
    if (placed.field) setErrors((x) => ({ ...x, [placed.field!]: placed.message }));
    else setError(placed.message);
  };

  const submit = async () => {
    const found = validateForm(form, creating);
    setErrors(found);
    setError(null);
    if (Object.keys(found).length) return;
    if (network && !Object.keys(patchBody(network, form)).length) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const saved = network
        ? await api.updateNetwork(network.id, patchBody(network, form))
        : await api.createNetwork(createBody(form));
      await onDone(saved.id);
    } catch (e) {
      fail(e);
    } finally {
      setSaving(false);
    }
  };

  const cidr = form.cidr.trim();
  const addresses = addressesIn(cidr);

  return (
    <PageAside label={creating ? 'New network' : `Edit ${network.name}`}>
      <div className={s.head}>
        <h2 className={s.title}>{creating ? 'New network' : network.name}</h2>
        <Tooltip content="Close" relationship="label">
          <Button appearance="subtle" shape="circular" size="small" icon={<Dismiss16Regular />} onClick={onClose} disabled={saving} />
        </Tooltip>
      </div>

      <form
        className={s.form}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className={s.section}>
          <Field label={{ children: 'Name', className: s.fieldLabel }} validationMessage={errors.name}>
            <Input value={form.name} onChange={(_e, d) => set('name', d.value)} maxLength={64} autoFocus={creating} />
          </Field>

          {creating ? (
            <>
              <Field
                label={{ children: 'Id', className: s.fieldLabel }}
                validationMessage={errors.id}
                hint={errors.id ? undefined : `net:${form.id || '…'} in firewall rules · cannot change later`}
              >
                <Input className={s.mono} value={form.id} onChange={(_e, d) => set('id', d.value)} maxLength={32} />
              </Field>
              <Field
                label={{ children: 'Address range', className: s.fieldLabel }}
                validationMessage={errors.cidr}
                hint={
                  errors.cidr ? undefined : (
                    <span className={cidr === nextFreeCidr ? s.hint : s.hintQuiet}>
                      {cidr
                        ? `${addresses} addresses${cidr === nextFreeCidr ? ' · next free, no overlap' : ''}`
                        : 'The next free /24 is taken when left empty.'}
                    </span>
                  )
                }
              >
                <Input className={s.mono} value={form.cidr} onChange={(_e, d) => set('cidr', d.value)} placeholder="10.88.4.0/24" />
              </Field>
            </>
          ) : (
            <dl className={s.readonly}>
              <dt className={s.k}>Id</dt>
              <dd className={s.v}>{network.id}</dd>
              <dt className={s.k}>Address range</dt>
              <dd className={s.v}>{network.cidr}</dd>
            </dl>
          )}

          <Switch
            checked={form.isolated}
            onChange={(_e, d) => set('isolated', d.checked)}
            label={{ children: 'Isolated', className: s.fieldLabel }}
          />
          <span className={s.toggleSub}>
            Reachable only through rules naming net:{form.id || '…'}. Takes effect at once, without a publish.
          </span>

          <Field label={{ children: 'Note', className: s.fieldLabel }} validationMessage={errors.note}>
            <Textarea value={form.note} onChange={(_e, d) => set('note', d.value)} maxLength={200} resize="vertical" />
          </Field>
        </div>

        {error && (
          <div className={s.section}>
            <MessageBar intent="error">
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          </div>
        )}

        <div className={s.actions}>
          <Button
            type="submit"
            appearance="primary"
            shape="circular"
            className={s.action}
            icon={saving ? <Spinner size="tiny" /> : creating ? <Add16Regular /> : undefined}
            disabled={saving}
          >
            {creating ? 'Create network' : 'Save'}
          </Button>
          <Button appearance="subtle" shape="circular" className={s.action} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <span className={s.spacer} />
          {!creating && (
            <Button
              appearance="subtle"
              shape="circular"
              className={s.action}
              icon={<Delete16Regular />}
              onClick={() => setConfirming(true)}
              disabled={saving}
            >
              Delete
            </Button>
          )}
        </div>
      </form>

      {network && (
        <DeleteDialog
          network={network}
          open={confirming}
          onClose={() => setConfirming(false)}
          onDeleted={async () => {
            setConfirming(false);
            await onDone(null);
          }}
        />
      )}
    </PageAside>
  );
}

/** DELETE /networks/:id behind a confirm; network_in_use (default network, devices, rules) shows here. */
function DeleteDialog({
  network,
  open,
  onClose,
  onDeleted,
}: {
  network: Network;
  open: boolean;
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const { api } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteNetwork(network.id);
      await onDeleted();
    } catch (e) {
      setError(networkError(e).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, d) => {
        if (!d.open && !busy) {
          setError(null);
          onClose();
        }
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Delete {network.name}?</DialogTitle>
          <DialogContent>
            <Text>
              {network.cidr} is freed for a new network. A network that still holds devices, is the default, or is named by
              a firewall rule cannot be deleted.
            </Text>
            {error && (
              <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalM }}>
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={() => {
                setError(null);
                onClose();
              }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button appearance="primary" onClick={() => void remove()} disabled={busy} icon={busy ? <Spinner size="tiny" /> : undefined}>
              Delete network
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
